'use client'
import React from 'react'
import Link from 'next/link'
import { cn } from "@/lib/utils"
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState } from 'react'
import { Gauge, List, FilePlus2, Upload, FileStack } from "lucide-react"
import { ArticleImportDialog } from '@/components/article/ArticleImportDialog'


import { useAuth } from '@/contexts/AuthContext'

const NavigationItems = ({ onImportClick }: { onImportClick: () => void }) => (
  <>
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link href="/user/dashboard" className={navigationMenuTriggerStyle()}>
          <Gauge className="h-4 w-4 mr-2" />
          ダッシュボード
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link href="/article" className={navigationMenuTriggerStyle()}>
          <List className="h-4 w-4 mr-2" />
          記事一覧
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link href="/article/new" className={navigationMenuTriggerStyle()}>
          <FilePlus2 className="h-4 w-4 mr-2" />
          新規記事作成
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link href="/article/bulk-new" className={navigationMenuTriggerStyle()}>
          <FileStack className="h-4 w-4 mr-2" />
          一括入稿
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
    <NavigationMenuItem>
      <button
        onClick={onImportClick}
        className={navigationMenuTriggerStyle()}
      >
        <Upload className="h-4 w-4 mr-2" />
        原稿インポート
      </button>
    </NavigationMenuItem>
  </>
)

export default function GlobalNavigation() {
  const { isLoggedIn } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)

  if (!isLoggedIn) {
    return null
  }

  const handleImportClick = () => {
    setShowImportDialog(true)
  }

  const handleMobileImportClick = () => {
    setIsOpen(false)
    setShowImportDialog(true)
  }

  return (
    <>
      {/* デスクトップナビゲーション */}
      <div className="hidden md:block">
        <NavigationMenu>
          <NavigationMenuList>
            <NavigationItems onImportClick={handleImportClick} />
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {/* モバイルナビゲーション */}
      <div className="md:hidden">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left">
            <div className="flex flex-col space-y-4 mt-4">
              <Link
                href="/user/dashboard"
                className="text-lg font-medium flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <Gauge className="h-5 w-5" />
                ダッシュボード
              </Link>
              <Link
                href="/article"
                className="text-lg font-medium flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <List className="h-5 w-5" />
                記事一覧
              </Link>
              <Link
                href="/article/new"
                className="text-lg font-medium flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <FilePlus2 className="h-5 w-5" />
                新規記事作成
              </Link>
              <Link
                href="/article/bulk-new"
                className="text-lg font-medium flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <FileStack className="h-5 w-5" />
                一括入稿
              </Link>
              <button
                className="text-lg font-medium flex items-center gap-2 text-left"
                onClick={handleMobileImportClick}
              >
                <Upload className="h-5 w-5" />
                原稿インポート
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* インポートダイアログ */}
      <ArticleImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
      />
    </>
  )
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  )
})
ListItem.displayName = "ListItem"
